import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  FallOutlined,
  RiseOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dateUtils';
import { getApiErrorMessage } from '../utils/apiError';
import { formatQuantitySet } from '../utils/commercialQuantity';
import { useFeedback } from '../utils/feedback';
import {
  fetchMonthlyInventoryReport,
} from '../modules/inventory/api/inventoryApi';
import type {
  InventoryVariantReport,
  MonthlyInventoryReport,
  QuantityReportBucket,
} from '../dto/inventory';

const { Text, Title } = Typography;

function quantityCell(
  bucket: QuantityReportBucket,
  language: string,
): React.ReactNode {
  return (
    <Space direction="vertical" size={0}>
      <Text>{formatQuantitySet(bucket, 'quantity', language, {
        fallbackQuantity: bucket.quantity,
        fallbackUnit: bucket.quantityUnit,
      })}</Text>
      <Text type="secondary">{formatCurrency(Number(bucket.valuePLN || 0), 'PLN')}</Text>
    </Space>
  );
}

const InventoryReports: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const feedback = useFeedback();
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [report, setReport] = useState<MonthlyInventoryReport | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await fetchMonthlyInventoryReport(
        selectedMonth.year(),
        selectedMonth.month() + 1,
      ));
    } catch (error: unknown) {
      setReport(null);
      feedback.error(getApiErrorMessage(error, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [feedback, selectedMonth, t]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const columns = [
    {
      title: t('inventory:fields.productName'),
      key: 'product',
      fixed: 'left' as const,
      width: 240,
      render: (_: unknown, row: InventoryVariantReport) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.productName || t('inventory:fields.productName')}</Text>
          <Text type="secondary">
            {[row.variantName, row.sku].filter(Boolean).join(' · ') || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: t('inventory:reports.openingStock'),
      dataIndex: 'opening',
      key: 'opening',
      width: 260,
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.receipts'),
      dataIndex: 'receipts',
      key: 'receipts',
      width: 260,
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.releases'),
      dataIndex: 'releases',
      key: 'releases',
      width: 260,
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.adjustments'),
      dataIndex: 'adjustments',
      key: 'adjustments',
      width: 260,
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
    {
      title: t('inventory:reports.closingStock'),
      dataIndex: 'closing',
      key: 'closing',
      width: 260,
      render: (bucket: QuantityReportBucket) => quantityCell(bucket, i18n.language),
    },
  ];

  const summary = report?.summary;
  const variants = report?.variants || [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/inventory')}>
            {t('common:back')}
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            {t('inventory:reports.title')}
          </Title>
        </Space>
        <DatePicker
          picker="month"
          value={selectedMonth}
          onChange={(date) => date && setSelectedMonth(date)}
          disabledDate={(current) => Boolean(current && current > dayjs().endOf('month'))}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
      ) : !report || variants.length === 0 ? (
        <Card>
          <Empty description={t('inventory:reports.noData')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 24 }}>
            <Descriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label={t('inventory:reports.reportPeriod')}>
                {selectedMonth.format('MMMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory:valuation.method')}>
                {t('inventory:valuation.fifo')}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory:reports.periodStart')}>
                {report.periodStart ? formatDate(report.periodStart) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory:reports.periodEnd')}>
                {report.periodEnd ? formatDate(report.periodEnd) : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} xl={6}>
              <Card><Statistic title={t('inventory:reports.openingBalance')} value={summary?.openingBalancePLN || 0} precision={2} suffix="PLN" /></Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card><Statistic title={t('inventory:reports.totalReceiptsValue')} value={summary?.receiptsPLN || 0} precision={2} suffix="PLN" valueStyle={{ color: '#3f8600' }} prefix={<RiseOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card><Statistic title={t('inventory:reports.totalReleasesValue')} value={summary?.releasesPLN || 0} precision={2} suffix="PLN" valueStyle={{ color: '#cf1322' }} prefix={<FallOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card><Statistic title={t('inventory:reports.closingBalance')} value={summary?.closingBalancePLN || 0} precision={2} suffix="PLN" valueStyle={{ color: '#1677ff' }} /></Card>
            </Col>
          </Row>

          <Card title={<Space><SwapOutlined />{t('inventory:reports.quantityLedger')}</Space>}>
            <Table
              dataSource={variants}
              columns={columns}
              rowKey="productVariantId"
              pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }}
              scroll={{ x: 1540 }}
              locale={{ emptyText: t('inventory:reports.noData') }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default InventoryReports;
